import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { DynamicQueryDecoratorService } from 'common/dynamicquery/dynamic-query-decorator.service';
import { DynamicQueryAdapterService } from 'common/dynamicquery/dynamic-query-adapter.service';
import { Subscription } from 'rxjs';
import { BackendFieldConverterService } from 'shared/services/backend-field-converter/backend-field-converter.service';
import { ServiceItem } from 'components/queue-view/live-workload-tab/models/service-item';
import { ClientItem } from 'components/queue-view/live-workload-tab/models/client-item';
import { AgentItem, AgentServiceItem } from 'components/queue-view/live-workload-tab/models/agent-item';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { OperatorStatus } from 'components/queue-view/live-workload-tab/models/operator-status';
import { DepartmentDropdownItem } from 'components/queue-view/models/dropdown-item.interface';
import { BcRestApi } from 'common/api/rest-api';
import { QueueViewCheckBoxHelper } from '../../queue-view-checkbox-helper';
import { BcAuth } from 'common/auth/auth.service';
import { ModalService } from 'shared/components/modal-dialog/modal-dialog.service';
import { ServiceAvailabilityCollectorService } from 'shared/services/service-availability-collector/service-availability-collector-service';
import { debounce, sum, values } from 'lodash';
import { AgentListTabService } from 'components/queue-view/live-workload-tab/agent-list-tab/agent-list-tab.service';
import { agentListItemsAnimation } from 'components/queue-view/live-workload-tab/agent-list-tab/agent-list-tab.animations';
import { AgentLoads } from 'common/api/models/agent-loads.model';
import { AgentListSortingHelper } from 'components/queue-view/live-workload-tab/agent-list-tab/agent-list-sorting-helper';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { AgentCapacities, Capacity } from 'common/api/models/agent-capacities.model';
import { TabSettings } from 'components/queue-view/live-workload-tab/interfaces/tab-settings';
import { ServiceColumn, ServiceColumns, ServiceHeaderTranslateTexts } from 'components/queue-view/live-workload-tab/models/column-config';
import { Sort, SortDirection } from 'components/queue-view/live-workload-tab/models/sort';
import { CapacityConstants } from 'components/queue-view/live-workload-tab/agent-list-tab/agent-list-tab.constants';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { AccountPermissionType } from 'common/common/constants/account-permission-type';
import { PollingService } from 'shared/services/polling-service/polling.service';
import { LiveWorkloadTabHelper } from 'components/queue-view/live-workload-tab/live-workload-tab.helper';

@Component({
  selector: 'app-agent-list-tab',
  templateUrl: './agent-list-tab.component.html',
  styleUrls: ['./agent-list-tab.component.scss'],
  animations: [
    agentListItemsAnimation
  ],
  encapsulation: ViewEncapsulation.None
})
export class AgentListTabComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  selectedDepartments: DepartmentDropdownItem[];

  @Output() navigateToOtherTab: EventEmitter<TabSettings> = new EventEmitter<TabSettings>();

  public serviceHeaderTranslateTexts = ServiceHeaderTranslateTexts;
  public sortDirection = SortDirection;
  public capacityConstants = CapacityConstants;

  public serviceHeaders: ServiceColumn[] = [];
  public displayedAgentList: AgentItem[] = [];
  public isListLoading: boolean = false;
  public anyChecked: boolean = false;
  public allChecked: boolean = false;
  public operator;
  public logOutConfirmationVisible: boolean = false;
  public loadSums = {};
  public capacitySums = {};
  public activeSort: Sort = { active: 'bestName', direction: SortDirection.ASC };
  public agentList: AgentItem[] = [];
  public includedServices: string[] = [];

  private _serviceInsertSubscription: Subscription;
  private _serviceDeleteSubscription: Subscription;
  private _serviceDoneSubscription: Subscription;
  private _clientDeleteSubscription: Subscription;
  private _loadSubscription: Subscription;
  private _capacities: AgentCapacities = {};
  private _loads: AgentLoads;
  private _filterValue = '';
  private _departmentIDs: string[];

  private readonly FILTER_DEBOUNCE_TIME = 300; // ms
  private readonly FILTER_DEBOUNCE_MAX_WAIT = 500; // ms

  constructor(private _dynamicQuery: DynamicQueryAdapterService,
              private _dynamicQueryDecorator: DynamicQueryDecoratorService,
              private _changeDetector: ChangeDetectorRef,
              private _backendFieldConverter: BackendFieldConverterService,
              private _bcRestApi: BcRestApi,
              private _bcAuth: BcAuth,
              private _bcModalService: ModalService,
              private _serviceCollectorService: ServiceAvailabilityCollectorService,
              private _agentListTabService: AgentListTabService,
              private _translateService: TranslateService,
              private _userPermissionService: UserPermissionService,
              private _pollingService: PollingService) {
  }

  async ngOnInit(): Promise<void> {
    this.operator = this._bcAuth.getSession();

    if (!this._getColumnSortingSettings()) {
      this._setColumnSortingSettings();
    }
    this.activeSort = this._getColumnSortingSettings();

    const services: WorkItemTypes[] = await this._serviceCollectorService.getAvailableServicesByWorkItemTypeValue();
    const servicesIncludedInOmni = this._serviceCollectorService.servicesIncludedInOmni();
    servicesIncludedInOmni.forEach((serviceType) => {
      const serviceName = this._backendFieldConverter.serviceTypeConverter(serviceType.toString());
      this.includedServices.push(serviceName);
    });
    this.serviceHeaders = ServiceColumns.filter(serviceHeader => services.includes(serviceHeader.workItemType));
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.selectedDepartments) {
      if (changes.selectedDepartments.currentValue !== changes.selectedDepartments.previousValue) {
        this.allChecked = false;
        await this._initSubscriptions();
      }
    }
  }

  ngOnDestroy(): void {
    this._clearSubscriptions();
  }

  public isOmniType(workItemType): boolean {
    return workItemType === WorkItemTypes.OMNI;
  }

  public getStatusType(statusType: number): string {
    return this._backendFieldConverter.statusTypeConverter(statusType);
  }

  public getClassByStatus(statusType: OperatorStatus): string {
    switch (statusType) {
      case OperatorStatus.OFFLINE:
        return 'offline-status';
      case OperatorStatus.AWAY:
        return 'away-status';
      case OperatorStatus.AVAILABLE:
        return 'available-status';
      default:
        return '';
    }
  }

  public getTooltip(workItemType: WorkItemTypes): string {
    return LiveWorkloadTabHelper.getTooltip('agentWorkload', workItemType);
  }

  public getIconByWorkItemType(workItemType: WorkItemTypes): string {
    return LiveWorkloadTabHelper.getIconByWorkItemType(workItemType);
  }

  public filterList(filterText: string): void {
    this._filterValue = filterText.trim().toLowerCase();
    this._debouncedUpdateList();
  }

  public hasAgents(): boolean {
    return this.displayedAgentList.length > 0;
  }

  public isLogoutActionAvailable(): boolean {
    return this._userPermissionService.hasAccountPermissionFor(AccountPermissionType.LogOffPeer);
  }

  public getSelectedClientIds(): string[] {
    return this.displayedAgentList
      .filter(queueElem => queueElem.checked)
      .map(elem => elem.clientId);
  }

  public checkAllOrNone(): void {
    this.allChecked = !this._areAllRowsChecked();
    this._toggleAllSelection(this.allChecked);
    this.anyChecked = this._isAnyRowChecked();
    this._changeDetector.detectChanges();
  }

  public changeChecked(row): void {
    row.checked = !row.checked;
    this._updateCheckedState();
  }

  public isOwnClient(agent): boolean {
    if (agent) {
      return agent.clientId === this.operator.clientId;
    }
  }

  public isOtherClientInTheList(): boolean {
    return !!this.displayedAgentList.some(agent => agent.clientId !== this.operator.clientId);
  }

  public isFooterAvailable(): boolean {
    return this.isLogoutActionAvailable() && this.hasAgents() && this.isOtherClientInTheList();
  }

  public showLogOutConfirmation(): void {
    if (!this.logOutConfirmationVisible) {
      this.logOutConfirmationVisible = true;

      const modalOptions = {
        headerText: this._getHeaderText(),
        messageBody: '',
        confirmButton: {
          text: 'agentListTab.buttons.forceLogOut',
          clickEvent: () => this.modalConfirmAction()
        },
        cancelButton: {
          text: 'agentListTab.logOutConfirmationModal.cancel'
        },
        resultFunction: () => this.modalCloseAction(),
        closeable: false
      };
      this._bcModalService.show(modalOptions);
    }
  }

  public async modalConfirmAction(): Promise<void> {
    await this._logOut();
  }

  public modalCloseAction(): void {
    this._closeConfirmation();
  }

  public getQueueSize(serviceType: string): number {
    return this._loads.queueSize[this._agentListTabService.convertServiceTypeToLoadType(serviceType)];
  }

  public getLoadPercentage(agent: AgentItem, serviceType: string): number {
    if (agent.services[serviceType] && agent.services[serviceType].capacity !== 0) {
      return (agent.services[serviceType].load / agent.services[serviceType].capacity);
    }

    return null;
  }

  public showCapacity(agent: AgentItem, serviceType: string): boolean {
    return agent.services[serviceType].capacity !== CapacityConstants.N_A;
  }

  public isUnlimitedCapacity(agent: AgentItem, serviceType: string): boolean {
    return agent.services[serviceType] && !agent.services[serviceType].capacity;
  }

  public getElapsedTime(timeStamp: string): string {
    const duration: moment.Duration = moment.duration(moment().diff(timeStamp));

    if (duration.asHours() < 1 && duration.asMinutes() < 1) {
      return this._translateService.instant('agentListTab.elapsedTime.lessThanAMinute');
    } else if (duration.asHours() < 1) {
      return Math.floor(duration.asMinutes()) + moment.utc(duration.asMilliseconds()).format('[m]');
    } else {
      return Math.floor(duration.asHours()) + moment.utc(duration.asMilliseconds()).format('[h] mm[m]');
    }
  }

  public isColumnHeaderSelected(columnName): boolean {
    return this.activeSort.active === columnName;
  }

  public sortByColumnName(columnName: string): void {
    if (this.activeSort.active === columnName) {
      this.activeSort.direction = this.activeSort.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
    } else {
      this.activeSort.active = columnName;
      this.activeSort.direction = SortDirection.ASC;
    }

    this._sortData(this.agentList);
    this._updateList();
    this._setColumnSortingSettings();
  }

  public navigateToAssignedToAgentTab(agent: AgentItem, workItemType: string): void {
    if (this.isClickable(agent, workItemType)) {
      if (WorkItemTypes.FACEBOOK === workItemType || WorkItemTypes.ASYNC_MESSAGE === workItemType) {
        workItemType = WorkItemTypes.SMS;
      }
      this.navigateToOtherTab.emit({ tab: 'ASSIGNED', workItemType, operatorName: agent.bestName });
    }
  }

  public isClickable(agent: AgentItem, serviceType: string): boolean {
    if (serviceType === WorkItemTypes.OMNI) {
      return false;
    }
    return agent.services[serviceType] ? agent.services[serviceType].load > 0 : false;
  }

  private _debouncedUpdateList = debounce(
    () => {
      this._updateList();
      this._toggleAllSelection(false);
      this._updateCheckedState();
    },
    this.FILTER_DEBOUNCE_TIME,
    {
      leading: true, trailing: true,
      maxWait: this.FILTER_DEBOUNCE_MAX_WAIT
    }
  );

  private _applyFilter(agentList: AgentItem[]): AgentItem[] {
    if (this._filterValue) {
      return agentList.filter(agent => agent.bestName.toLowerCase().includes(this._filterValue));
    }

    return [...agentList];
  }

  private _setColumnSortingSettings(): void {
    this._agentListTabService.setColumnSortingSettings(this.activeSort);
  }

  private _getColumnSortingSettings(): Sort {
    return this._agentListTabService.getColumnSortingSettings();
  }

  private _getSelectedClients(): AgentItem[] {
    return this.agentList ? this.agentList.filter(client => client.checked === true) : [];
  }

  private _closeConfirmation(): void {
    this.logOutConfirmationVisible = false;
    this._bcModalService.close();
  }

  private async _logOut(): Promise<void> {
    const clientIds = this.getSelectedClientIds();
    await this._bcRestApi.logOutClients(clientIds);
    this._toggleAllSelection(false);
    this._updateCheckedState();
  }

  private _getHeaderText(): string {
    const selectedClients = this._getSelectedClients();
    return selectedClients.length > 1 ? 'agentListTab.logOutConfirmationModal.logOutConfirmationPlural' : 'agentListTab.logOutConfirmationModal.logOutConfirmationSingular';
  }

  private _areAllRowsChecked(): boolean {
    return QueueViewCheckBoxHelper.areAllRowsChecked(this._removeOwnClientFromList());
  }

  private _isAnyRowChecked(): boolean {
    return QueueViewCheckBoxHelper.isAnyRowChecked(this.displayedAgentList);
  }

  private _toggleAllSelection(shouldBeSelected): void {
    QueueViewCheckBoxHelper.toggleAllSelection(this._removeOwnClientFromList(), shouldBeSelected);
  }

  private _removeOwnClientFromList(): AgentItem[] {
    return this.displayedAgentList.filter(item => item.clientId !== this.operator.clientId);
  }

  private _updateCheckedState(): void {
    this.allChecked = this._areAllRowsChecked();
    this.anyChecked = this._isAnyRowChecked();
  }

  private _clientDeleted(clientItem: ClientItem): void {
    const clientIndex = this.agentList.findIndex(agent => agent.clientId === clientItem.ClientID);

    if (clientIndex > -1) {
      this.agentList.splice(clientIndex, 1);
      this._updateList();
    }
  }

  private async _serviceInserted(serviceItem: ServiceItem): Promise<void> {
    const clientIndex = this.agentList.findIndex(agent => agent.clientId === serviceItem.ClientID);
    const serviceType = this._getServiceType(serviceItem.ServiceTypeID);

    if (!this.agentList[clientIndex]) {
      const agentItem: AgentItem = {
        clientId: serviceItem.ClientID,
        operatorId: serviceItem.OperatorID,
        bestName: serviceItem.OperatorName ? serviceItem.OperatorName : serviceItem.UserName,
        services: {
          [serviceType]: {
            loading: true
          }
        }
      };

      this.agentList.push(agentItem);

      // show item with skeletons
      this._sortData(this.agentList);
      this._updateList();

      await this._updateLoadForUnknownOperator(serviceItem.OperatorID);

      // The async part is separated to make sure no duplicate items are added while the data is fetching
      agentItem.services[serviceType] = await this._createServiceDataForServiceType(serviceItem, serviceItem.ServiceTypeID);
    } else {
      if (!this.agentList[clientIndex].services[serviceType]) {
        this.agentList[clientIndex].services[serviceType] = { loading: true };

        // show item with skeletons
        this._updateList();

        this.agentList[clientIndex].services[serviceType] = await this._createServiceDataForServiceType(serviceItem, serviceItem.ServiceTypeID);
      }
    }

    this._sortData(this.agentList);
    this._updateList();
    this._calculateSums(serviceType);
  }

  private async _createServiceDataForServiceType(serviceItem: ServiceItem, serviceTypeId: string): Promise<AgentServiceItem> {
    const [capacity, load] = await Promise.all([
      this._getCapacity(serviceItem.OperatorID, this._getServiceType(serviceTypeId)),
      this._getLoad(serviceItem.OperatorID, this._getServiceType(serviceTypeId))
    ]);

    return {
      customStatusName: serviceItem.CustomStatusName,
      statusType: this._backendFieldConverter.statusTypeConverter(serviceItem.StatusType),
      serviceType: this._getServiceType(serviceTypeId),
      created: serviceItem.Created,
      capacity,
      load,
      loading: false
    };
  }

  private _serviceDeleted(serviceItem: ServiceItem): void {
    const clientIndex = this.agentList.findIndex(agent => agent.clientId === serviceItem.ClientID);

    if (this.agentList[clientIndex] && this.agentList[clientIndex].services[this._getServiceType(serviceItem.ServiceTypeID)]) {
      delete this.agentList[clientIndex].services[this._getServiceType(serviceItem.ServiceTypeID)];

      this._updateList();
    }
  }

  private _updateList(): void {
    this.displayedAgentList = this._applyFilter(this.agentList);
    this._changeDetector.detectChanges();
  }

  private _getServiceType(serviceType: string): string {
    return this._backendFieldConverter.serviceTypeConverter(serviceType);
  }

  private _getSelectedDepartmentIDs(): string[] {
    if (this.selectedDepartments) {
      return this.selectedDepartments.map(department => department.id);
    }
  }

  private async _initSubscriptions(): Promise<void> {
    this.isListLoading = true;
    this._resetAgentList();
    this._clearSubscriptions();

    this._departmentIDs = this._getSelectedDepartmentIDs();

    if (this._departmentIDs.length) {
      await this._initCapacities();
      await this._initLoadSubscriptions(this._departmentIDs);

      const activeServiceSignInsObservable = this._dynamicQuery.getQuery('getActiveServiceSignIns', { DepartmentIDs: this._departmentIDs });
      this._dynamicQueryDecorator.create(activeServiceSignInsObservable, item => item);

      this._serviceInsertSubscription = activeServiceSignInsObservable.inserted()
        .subscribe(dynamicQueryEvent => this._serviceInserted(dynamicQueryEvent.data));

      this._serviceDeleteSubscription = activeServiceSignInsObservable.deleted()
        .subscribe(dynamicQueryEvent => this._serviceDeleted(dynamicQueryEvent.data));

      this._serviceDoneSubscription = activeServiceSignInsObservable.done()
        .subscribe(() => this._queryDone());

      const clientSignInsObservable = this._dynamicQuery.getQuery('activeClients', null);
      this._dynamicQueryDecorator.create(clientSignInsObservable, item => item);

      this._clientDeleteSubscription = clientSignInsObservable.deleted()
        .subscribe(dynamicQueryEvent => this._clientDeleted(dynamicQueryEvent.data));
    } else {
      this._queryDone();
      this._updateList();
    }
  }

  private _loadsUpdated(loads: AgentLoads): void {
    this._loads = loads;
    this._loads.operatorLoads.forEach(operatorLoad => {
      const agentItems = this.agentList.filter(agent => agent.operatorId === operatorLoad.operatorId);
      agentItems.forEach(agentItem => {
        if (agentItem) {
          Object.keys(agentItem.services).forEach(serviceType => {
            if (agentItem.services[serviceType]) {
              agentItem.services[serviceType].load = operatorLoad.load[this._agentListTabService.convertServiceTypeToLoadType(serviceType)];
            }
          });
        }
      });
    });
    this._updateList();
    this._recalculateAllSums();
  }

  private _resetAgentList(): void {
    this.agentList.length = 0;
  }

  private _queryDone(): void {
    this.isListLoading = false;
  }

  private _clearSubscriptions(): void {
    if (this._serviceInsertSubscription) {
      this._serviceInsertSubscription.unsubscribe();
      this._serviceInsertSubscription = null;
    }

    if (this._serviceDeleteSubscription) {
      this._serviceDeleteSubscription.unsubscribe();
      this._serviceDeleteSubscription = null;
    }

    if (this._serviceDoneSubscription) {
      this._serviceDoneSubscription.unsubscribe();
      this._serviceDoneSubscription = null;
    }

    if (this._clientDeleteSubscription) {
      this._clientDeleteSubscription.unsubscribe();
      this._clientDeleteSubscription = null;
    }

    if (this._loadSubscription) {
      this._loadSubscription.unsubscribe();
    }
  }

  private _recalculateAllSums(): void {
    this.serviceHeaders.forEach((serviceHeader) => {
      this._calculateSums(serviceHeader.workItemType);
    });
  }

  private _calculateSums(serviceType: string): void {
    this.loadSums[serviceType] = this._getSumOfLoads(serviceType);
    this.capacitySums[serviceType] = this._getSumOfCapacities(serviceType);
  }

  private _getSumOfLoads(serviceType: string): number {
    const loads = {};
    this.agentList.forEach(agent => {
      if (!loads[agent.operatorId] && agent.services[serviceType]) {
        loads[agent.operatorId] = agent.services[serviceType].load;
      }
    });
    return sum(values(loads));
  }

  private _getSumOfCapacities(serviceType: string): number {
    const capacities = {};
    this.agentList.forEach(agent => {
      if (!capacities[agent.operatorId] && agent.services[serviceType]) {
        capacities[agent.operatorId] = agent.services[serviceType].capacity;
      }
    });

    const capacityValues = values(capacities);
    if (this._hasUnlimitedCapacity(capacityValues)) {
      return CapacityConstants.UNLIMITED;
    }

    const filteredCapacityValues = this._filterUnknownCapacities(capacityValues);
    if (filteredCapacityValues.length === 0) {
      return CapacityConstants.N_A;
    }

    return sum(filteredCapacityValues);
  }

  private _filterUnknownCapacities(capacityValues: unknown[]) {
    return capacityValues.filter(value => value !== CapacityConstants.N_A);
  }

  private _hasUnlimitedCapacity(capacityValues: unknown[]) {
    return capacityValues.filter(value => !value).length > 0;
  }

  private async _getLoad(operatorId: string, serviceType: string): Promise<number> {
    let operatorLoad = this._loads.operatorLoads.find(load => load.operatorId === operatorId);

    if (!operatorLoad) {
      return 0;
    }

    return operatorLoad.load[this._agentListTabService.convertServiceTypeToLoadType(serviceType)];
  }

  private async _updateLoadForUnknownOperator(operatorId: string): Promise<void> {
    let operatorLoad = this._loads.operatorLoads.find(load => load.operatorId === operatorId);

    if (!operatorLoad) {
      await this._refresh(this._departmentIDs);
      this._loadsUpdated(this._loads);
    }
  }

  private async _initCapacities(): Promise<void> {
    this._capacities = await this._agentListTabService.getCapacitiesByDepartmentIds(this._getSelectedDepartmentIDs());
  }

  private async _initLoadSubscriptions(departmentIds: string[]): Promise<AgentLoads> {
    if (this._loadSubscription) {
      this._loadSubscription.unsubscribe();
    }

    const observable = this._agentListTabService.observeLoad(departmentIds);
    this._loadSubscription = observable.subscribe(loads => this._loadsUpdated(loads));
    this._loads = await this._pollingService.firstToPromise(observable);
    return this._loads;
  }

  private async _getCapacitiesForOperator(operatorId: string): Promise<Capacity> {
    const capacities = await this._agentListTabService.getCapacitiesByOperatorId(operatorId);
    this._capacities = { ...this._capacities, ...capacities };
    return this._capacities[operatorId];
  }

  private async _getCapacity(operatorId: string, serviceType: string): Promise<number> {
    let capacities = this._capacities[operatorId];

    if (!capacities) {
      capacities = await this._getCapacitiesForOperator(operatorId) || { chat: null, email: null, textChat: null, omniQueue: null };
    }

    switch (serviceType) {
      case WorkItemTypes.CHAT:
        return capacities.chat;
      case WorkItemTypes.EMAIL:
        return capacities.email;
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
        return capacities.textChat;
      case WorkItemTypes.OMNI:
        return capacities.omniQueue;
      default:
        return null;
    }
  }

  private _sortData(data: AgentItem[]): AgentItem[] {
    return AgentListSortingHelper.sort(data, this.activeSort);
  }

  private _refresh(departmentIds): Promise<AgentLoads> {
    return this._initLoadSubscriptions(departmentIds);
  }
}
